import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Space, Tag, Typography } from "antd";
import { Segmented } from "@gull/ui";
import {
  ASSESSMENT_OFFER_RATES,
  buildOfferOption,
  formatGrams,
  formatKarat,
  formatNok,
  type AssessmentSettlementType,
  type DeliveryStatusResponse,
} from "@gull/shared";

const { Text } = Typography;

const offerRowStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  gap: 16,
  borderBottom: "1px solid #f1f5f9",
  paddingBottom: 10,
};

interface DeliveryOfferCardProps {
  delivery: DeliveryStatusResponse;
  /** Whether the reader holds a token — it gates answering, not merely the buttons' appearance. */
  canAct: boolean;
  responding: "ACCEPTED" | "DECLINED" | null;
  onRespond: (response: "ACCEPTED" | "DECLINED", settlementType: AssessmentSettlementType) => void;
}

/**
 * The offer, and everything that depends on how it will be settled.
 *
 * A reference-only reader is served no figures at all, so their absence does not mean there is no
 * offer — the state code and the recorded response are what survive that read, and the card still
 * renders to carry the "open the link from your e-mail" notice.
 */
export function DeliveryOfferCard({
  delivery,
  canAct,
  responding,
  onRespond,
}: DeliveryOfferCardProps) {
  const [settlementType, setSettlementType] = useState<AssessmentSettlementType>(
    delivery.assessmentSettlementType ?? "PAYOUT",
  );

  useEffect(() => {
    if (delivery.assessmentSettlementType) setSettlementType(delivery.assessmentSettlementType);
  }, [delivery.assessmentSettlementType]);

  // The deduction the offer was priced with, so the rows and the total the customer accepts agree.
  const deductionNok = delivery.assessmentDeductionNok ?? 0;

  const offerOptions = useMemo(
    () => ({
      PAYOUT: buildOfferOption(
        delivery.assessmentRows,
        ASSESSMENT_OFFER_RATES.PAYOUT,
        delivery.assessmentPayoutValueOverrideNok,
        deductionNok,
      ),
      GIFT_CARD: buildOfferOption(
        delivery.assessmentRows,
        ASSESSMENT_OFFER_RATES.GIFT_CARD,
        delivery.assessmentGiftCardValueOverrideNok,
        deductionNok,
      ),
    }),
    [delivery, deductionNok],
  );
  const selectedOffer = offerOptions[settlementType];

  const hasOffer =
    delivery.assessmentTotalValue != null ||
    delivery.assessmentResponse != null ||
    delivery.status.code === "OFFER_SENT";
  if (!hasOffer) return null;

  return (
    <Card title="Tilbud på innleveringen">
      <Space direction="vertical" size="middle" style={{ display: "flex" }}>
        {delivery.assessmentTotalValue != null ? (
          <>
            {!delivery.assessmentResponse && offerOptions ? (
              <>
                <Alert
                  type="info"
                  showIcon
                  message="Velg hvordan du vil få oppgjøret"
                  description="Du kan velge mellom utbetaling til bankkonto eller gavekort hos David-Andersen før du godkjenner tilbudet."
                />
                <Segmented
                  variant="choice"
                  block
                  size="large"
                  value={settlementType}
                  onChange={(value) => setSettlementType(value as AssessmentSettlementType)}
                  options={[
                    {
                      label: `Utbetaling ${formatNok(offerOptions.PAYOUT.totalValue)}`,
                      value: "PAYOUT",
                    },
                    {
                      label: `Gavekort ${formatNok(offerOptions.GIFT_CARD.totalValue)}`,
                      value: "GIFT_CARD",
                    },
                  ]}
                />
              </>
            ) : null}
            {(selectedOffer?.rows ?? delivery.assessmentRows).map((row) => (
              <div key={row.karat} style={offerRowStyle}>
                <Space wrap>
                  <Tag color="gold">{formatKarat(row.karat)}</Tag>
                  <Text>Vekt veid gull: {formatGrams(row.weightGrams)}</Text>
                </Space>
                <Text strong>{formatNok(row.valueNok)}</Text>
              </div>
            ))}
            {deductionNok > 0 ? (
              <div style={offerRowStyle}>
                <Text>Fratrekk steinfjerning</Text>
                <Text strong>{`−${formatNok(deductionNok)}`}</Text>
              </div>
            ) : null}
            <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
              <Text strong>Totalt tilbud</Text>
              <Text strong>
                {formatNok(selectedOffer?.totalValue ?? delivery.assessmentTotalValue)}
              </Text>
            </div>
          </>
        ) : null}
        {/* Outside the figures block on purpose: a reader without the token is sent the message and
            the deadline but no kroner, so nesting these under the total hid them from exactly the
            customer who arrived from the receipt link. */}
        {delivery.assessmentComment ? (
          <Alert
            type="info"
            showIcon
            message="Kommentar"
            description={delivery.assessmentComment}
          />
        ) : null}
        {delivery.assessmentExpiresAt ? (
          <Text type="secondary">
            Gyldig til{" "}
            {new Date(delivery.assessmentExpiresAt).toLocaleString("nb-NO", {
              timeZone: "Europe/Oslo",
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </Text>
        ) : null}
        {delivery.assessmentResponse ? (
          <Alert
            className="neutral-status-alert"
            icon={<InfoCircleOutlined />}
            showIcon
            message={
              delivery.assessmentResponse === "ACCEPTED"
                ? "Du har godkjent tilbudet"
                : "Du har avslått tilbudet"
            }
            description={
              delivery.assessmentResponse === "ACCEPTED"
                ? delivery.assessmentSettlementType === "GIFT_CARD"
                  ? "Saken er ferdig hos David-Andersen. Vi følger opp gavekortet videre."
                  : "Saken er ferdig hos David-Andersen og venter på manuell utbetaling."
                : "Du får e-post når innleveringen er klargjort for henting. Den må hentes innen 3 måneder etter at e-posten er sendt."
            }
          />
        ) : canAct ? (
          <Space wrap>
            <Button
              type="primary"
              loading={responding === "ACCEPTED"}
              disabled={responding !== null}
              onClick={() => onRespond("ACCEPTED", settlementType)}
            >
              Godkjenn tilbud
            </Button>
            <Button
              danger
              loading={responding === "DECLINED"}
              disabled={responding !== null}
              onClick={() => onRespond("DECLINED", settlementType)}
            >
              Avslå tilbud
            </Button>
          </Space>
        ) : (
          <Alert
            type="info"
            showIcon
            message="Åpne tilbudslenken fra e-posten for å svare"
            description="Av sikkerhetshensyn kan tilbud bare godkjennes eller avslås fra lenken du har fått på e-post."
          />
        )}
        {delivery.assessmentResponseComment ? (
          <Alert
            type="info"
            showIcon
            message="Din kommentar"
            description={delivery.assessmentResponseComment}
          />
        ) : null}
        {delivery.returnPayment ? (
          delivery.returnPayment.status === "CAPTURED" ? (
            <Alert
              type="success"
              showIcon
              message="Returbetaling mottatt"
              description="Vi sender smykkene tilbake til deg."
            />
          ) : delivery.returnPayment.checkoutUrl ? (
            <Alert
              type="warning"
              showIcon
              message="Betal for å få smykkene tilbake"
              description={
                <Space direction="vertical" size={8}>
                  <Text>
                    {delivery.returnPayment.amount != null
                      ? `Betal ${formatNok(delivery.returnPayment.amount)} for å få smykkene i retur.`
                      : "Betal for å få smykkene i retur."}
                  </Text>
                  <Button
                    type="primary"
                    onClick={() => window.location.assign(delivery.returnPayment!.checkoutUrl!)}
                  >
                    Betal for å få smykkene tilbake
                  </Button>
                </Space>
              }
            />
          ) : null
        ) : null}
      </Space>
    </Card>
  );
}
